import { Link } from 'react-router-dom';
import { UserCircleIcon, ArrowRightOnRectangleIcon, Bars3Icon, XMarkIcon, HomeModernIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useEffect, useState } from 'react';
import { getUserProfile } from '../services/userProfileService';
import { createLogger } from '../utils/logger';
import { UserType } from '../types/userTypes';

const logger = createLogger('Navbar');

export default function Navbar() {
  const { currentUser, logOut } = useAuth();
  const [profileImage, setProfileImage] = useState<string>('');
  const [imageError, setImageError] = useState(false);
  const [userType, setUserType] = useState<UserType | undefined>(undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  
  const handleLogout = async () => {
    try {
      await logOut();
      // No need to navigate - AuthContext will handle the state change
    } catch (error) {
      logger.error('Failed to log out', error);
    }
  };
  
  // Fetch user profile data when the user is logged in
  useEffect(() => {
    const fetchProfileData = async () => {
      if (currentUser) {
        try {
          const profile = await getUserProfile(currentUser);
          if (profile) {
            // Set profile image if available
            if (profile.profileImage) {
              setProfileImage(profile.profileImage);
              logger.info('Loaded profile image from API');
            } else {
              // Fallback to Firebase photoURL if no stored profile image
              setProfileImage(currentUser.photoURL || '');
            }
            
            // Set user type if available
            if (profile.preferences && profile.preferences.userType) {
              setUserType(profile.preferences.userType);
              logger.info(`User type set to: ${profile.preferences.userType}`);
            }
          } else {
            // Fallback to Firebase photoURL if no profile
            setProfileImage(currentUser.photoURL || '');
          }
        } catch (error) {
          logger.error('Error fetching profile data:', error);
          // Fallback to Firebase photoURL on error
          setProfileImage(currentUser.photoURL || '');
        }
      } else {
        setProfileImage('');
        setUserType(undefined);
      }
    };
    
    fetchProfileData();
  }, [currentUser]);

  // Reset the broken-image fallback whenever the source image changes
  useEffect(() => {
    setImageError(false);
  }, [profileImage]);
  
  const profilePath = userType === 'homeowner' || currentUser?.role === 'homeowner' ? '/homeowner-profile' : '/profile';
  const closeMenu = () => setMenuOpen(false);

  return (
    <nav className="sticky top-0 z-50 glass-strong shadow-glass border-b border-white border-opacity-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 sm:h-20 items-center">
          <Link to="/" onClick={closeMenu} className="flex items-center group transition-all duration-300 hover:scale-105">
            <div className="relative">
              <img 
                src="/images/SoberLivingSupport.png" 
                alt="SoberLivingSupport.com" 
                className="h-12 sm:h-16 w-auto mr-3 sm:mr-4 transition-all duration-300 group-hover:rotate-3 drop-shadow-lg" 
              />
              <div className="absolute inset-0 bg-gradient-to-r from-brand-accent to-brand-secondary opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300 rounded-full"></div>
            </div>
            <div className="flex flex-col">
              <span className="text-brand font-black text-xl sm:text-3xl tracking-tight leading-none hidden sm:block bg-gradient-to-r from-brand via-brand-medium to-brand-accent bg-clip-text text-transparent">
                SoberLivingSupport
              </span>
              <span className="text-brand-medium text-xs sm:text-sm font-medium tracking-wide hidden sm:block">
                Your Recovery Journey Starts Here
              </span>
              <span className="text-brand font-black text-lg tracking-tight sm:hidden bg-gradient-to-r from-brand to-brand-accent bg-clip-text text-transparent">
                SLS
              </span>
            </div>
          </Link>
          
          <div className="hidden md:flex items-center gap-2 mr-auto ml-8">
            <Link to="/listings" className="px-4 py-2 text-sm font-bold text-brand hover:bg-brand-soft rounded-full transition-colors">Browse homes</Link>
          </div>
          <div className="hidden md:flex items-center gap-3 sm:gap-4">
            {currentUser ? (
              <>
                <div className="hidden md:flex items-center gap-2 glass px-4 py-2 rounded-full border border-brand border-opacity-20">
                  <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
                  <span className="text-sm text-brand-dark font-semibold">
                    {currentUser.displayName || currentUser.email}
                  </span>
                </div>
                <Link 
                  to={profilePath}
                  className="relative group/profile"
                  title={userType === 'homeowner' ? "Homeowner Profile" : "User Profile"}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-brand-accent to-brand-secondary opacity-0 group-hover/profile:opacity-100 blur-lg transition-opacity duration-300 rounded-full"></div>
                  <div className="relative flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full border-2 border-brand-medium hover:border-brand-accent transition-all duration-300 bg-white shadow-soft hover:shadow-colored-lg transform hover:scale-110 active:scale-95">
                    {profileImage && !imageError ? (
                      <img 
                        src={profileImage} 
                        alt="User avatar" 
                        className="w-full h-full rounded-full object-cover" 
                        onError={() => setImageError(true)}
                      />
                    ) : (
                      <UserCircleIcon className="w-8 h-8 sm:w-10 sm:h-10 text-brand-medium" />
                    )}
                  </div>
                </Link>
                <button 
                  onClick={handleLogout}
                  className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-error to-red-600 text-white shadow-soft hover:shadow-large transition-all duration-300 transform hover:scale-110 active:scale-95 group/logout"
                  title="Sign out"
                >
                  <ArrowRightOnRectangleIcon className="w-6 h-6 sm:w-7 sm:h-7 group-hover/logout:rotate-12 transition-transform duration-300" />
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/signin" 
                  className="px-5 py-2.5 sm:px-7 sm:py-3 rounded-full text-sm sm:text-base font-bold text-brand border-2 border-brand bg-white hover:bg-brand hover:text-white shadow-soft hover:shadow-colored-lg transition-all duration-300 transform hover:scale-105 active:scale-95"
                >
                  Sign In
                </Link>
                <Link 
                  to="/signup" 
                  className="hidden sm:block px-5 py-2.5 sm:px-7 sm:py-3 rounded-full text-sm sm:text-base font-bold text-white bg-gradient-to-r from-brand to-brand-medium hover:from-brand-medium hover:to-brand-accent shadow-colored hover:shadow-colored-lg transition-all duration-300 transform hover:scale-105 active:scale-95"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
          <button type="button" className="md:hidden inline-flex w-11 h-11 items-center justify-center rounded-xl border border-brand/15 bg-white text-brand shadow-soft" onClick={() => setMenuOpen((open) => !open)} aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'} aria-expanded={menuOpen}>
            {menuOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden pb-4 pt-1">
            <div className="grid gap-2 rounded-2xl border border-brand/10 bg-white p-2 shadow-medium">
              <Link to="/listings" onClick={closeMenu} className="flex min-h-12 items-center gap-3 rounded-xl px-4 font-bold text-brand hover:bg-brand-soft"><HomeModernIcon className="h-5 w-5" />Browse homes</Link>
              {currentUser ? <>
                <Link to={profilePath} onClick={closeMenu} className="flex min-h-12 items-center gap-3 rounded-xl px-4 font-bold text-brand hover:bg-brand-soft"><UserCircleIcon className="h-5 w-5" />My profile</Link>
                <button onClick={() => { closeMenu(); handleLogout(); }} className="flex min-h-12 items-center gap-3 rounded-xl px-4 text-left font-bold text-red-700 hover:bg-red-50"><ArrowRightOnRectangleIcon className="h-5 w-5" />Sign out</button>
              </> : <>
                <Link to="/signin" onClick={closeMenu} className="flex min-h-12 items-center rounded-xl px-4 font-bold text-brand hover:bg-brand-soft">Sign in</Link>
                <Link to="/signup" onClick={closeMenu} className="flex min-h-12 items-center rounded-xl bg-brand px-4 font-bold text-white">Create account</Link>
              </>}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
